/**
 * Stored guest photos: writing them, handing out URLs, and cleaning up.
 *
 * A stored plan names photos by asset id only. What the editor shows is an
 * object URL, and those are per tab and die with it — so they are minted here,
 * once per asset per tab, and never written anywhere.
 */

import { newId } from '../ids'
import { type StoredAsset, asStorageError, getDb, nowIso } from './db'
import type { ProcessedPhoto } from './images'

/** A day: long enough for undo and for another open tab still pointing at a photo. */
export const GARBAGE_GRACE_MS = 24 * 60 * 60 * 1000

const urls = new Map<string, string>()

export async function storePhoto(
  photo: ProcessedPhoto,
): Promise<{ asset: StoredAsset; url: string }> {
  const asset: StoredAsset = {
    id: newId(),
    blob: photo.blob,
    type: photo.blob.type || 'image/jpeg',
    width: photo.width,
    height: photo.height,
    createdAt: nowIso(),
  }
  try {
    const db = await getDb()
    await db.put('assets', asset)
  } catch (error) {
    throw asStorageError(error)
  }
  const url = URL.createObjectURL(asset.blob)
  urls.set(asset.id, url)
  return { asset, url }
}

/** Object URLs for the given photos, read in one transaction. Missing ones are absent. */
export async function photoUrlsFor(assetIds: Iterable<string>): Promise<Map<string, string>> {
  const out = new Map<string, string>()
  const missing: string[] = []
  for (const id of new Set(assetIds)) {
    const known = urls.get(id)
    if (known) out.set(id, known)
    else missing.push(id)
  }
  if (missing.length === 0) return out

  const db = await getDb()
  const tx = db.transaction('assets', 'readonly')
  const found = await Promise.all(missing.map((id) => tx.store.get(id)))
  await tx.done
  for (const asset of found) {
    if (!asset) continue
    const url = URL.createObjectURL(asset.blob)
    urls.set(asset.id, url)
    out.set(asset.id, url)
  }
  return out
}

/** The stored records themselves — for backups, which need the bytes. */
export async function readAssets(assetIds: Iterable<string>): Promise<StoredAsset[]> {
  const db = await getDb()
  const tx = db.transaction('assets', 'readonly')
  const found = await Promise.all([...new Set(assetIds)].map((id) => tx.store.get(id)))
  await tx.done
  return found.filter((asset): asset is StoredAsset => Boolean(asset))
}

/**
 * Delete photos that no plan refers to any more — replaced, removed, or left
 * behind by a deleted plan. Only ones older than the grace period: a photo
 * uploaded a moment ago is not referenced until the next autosave, and an undo
 * can bring back a reference that a save had dropped.
 *
 * @returns how many were deleted
 */
export async function collectGarbage(
  options: { now?: number; graceMs?: number } = {},
): Promise<number> {
  const now = options.now ?? Date.now()
  const cutoff = now - (options.graceMs ?? GARBAGE_GRACE_MS)
  const db = await getDb()
  const tx = db.transaction(['plans', 'assets'], 'readwrite')
  const plans = await tx.objectStore('plans').getAll()
  const referenced = new Set<string>()
  for (const plan of plans) {
    for (const guest of plan.guests) {
      if (guest.photoAssetId) referenced.add(guest.photoAssetId)
    }
  }

  let deleted = 0
  let cursor = await tx.objectStore('assets').openCursor()
  while (cursor) {
    const asset = cursor.value
    const born = Date.parse(asset.createdAt)
    if (!referenced.has(asset.id)) {
      if (!(born <= now)) {
        // Dated in the future, or not readably at all — a clock that ran back,
        // or a restore before restores stamped their own date. The grace
        // period starts now rather than never.
        await cursor.update({ ...asset, createdAt: new Date(now).toISOString() })
      } else if (born < cutoff) {
        await cursor.delete()
        const url = urls.get(asset.id)
        if (url) URL.revokeObjectURL(url)
        urls.delete(asset.id)
        deleted += 1
      }
    }
    cursor = await cursor.continue()
  }
  await tx.done
  return deleted
}

/** Tests: forget every minted URL. */
export function forgetPhotoUrls(): void {
  for (const url of urls.values()) URL.revokeObjectURL(url)
  urls.clear()
}
